/**
 * useResolutionScale Hook
 * 
 * React hook for detecting and responding to screen resolution changes.
 * Provides scale factor and resolution information to components.
 * 
 * Usage:
 * const { scaleFactor, tier, is4K, isAssemblyLine } = useResolutionScale();
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getResolutionTier,
  getScaleFactor,
  getResolutionInfo,
  is4KOrHigher,
  isAssemblyLineResolution,
  RESOLUTION_TIERS,
} from '../theme/resolutionConfig';

/**
 * Hook options interface
 * @typedef {Object} UseResolutionScaleOptions
 * @property {number} debounceMs - Debounce delay for resize events (default: 150ms)
 * @property {boolean} enableLogging - Enable console logging for debugging (default: false)
 */

/**
 * Custom hook for resolution-based scaling
 * @param {UseResolutionScaleOptions} options - Hook configuration
 * @returns {Object} Resolution information and utilities
 */
export const useResolutionScale = (options = {}) => {
  const {
    debounceMs = 150,
    enableLogging = false,
  } = options;

  // ============================================
  // STATE
  // ============================================
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1920
  );
  
  const [screenHeight, setScreenHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 1080
  );

  // ============================================
  // DERIVED VALUES (Memoized for performance)
  // ============================================
  const resolutionInfo = useMemo(() => {
    return getResolutionInfo(screenWidth);
  }, [screenWidth]);

  const scaleFactor = useMemo(() => {
    return getScaleFactor(screenWidth);
  }, [screenWidth]);

  const tier = useMemo(() => {
    return getResolutionTier(screenWidth);
  }, [screenWidth]);

  const is4K = useMemo(() => {
    return is4KOrHigher(screenWidth);
  }, [screenWidth]);

  const isAssemblyLine = useMemo(() => {
    return isAssemblyLineResolution(screenWidth);
  }, [screenWidth]);

  // ============================================
  // RESOLUTION DETECTION
  // ============================================
  const updateDimensions = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    setScreenWidth(width);
    setScreenHeight(height);
    
    if (enableLogging) {
    }
  }, [enableLogging]);

  // ============================================
  // DEBOUNCED RESIZE HANDLER
  // ============================================
  useEffect(() => {
    let timeoutId = null;

    const handleResize = () => {
      // Clear existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Set new timeout
      timeoutId = setTimeout(() => {
        updateDimensions();
      }, debounceMs);
    };

    // Initial update
    updateDimensions();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [debounceMs, updateDimensions]);

  // ============================================
  // ORIENTATION DETECTION
  // ============================================
  const orientation = useMemo(() => {
    return screenWidth > screenHeight ? 'landscape' : 'portrait';
  }, [screenWidth, screenHeight]);

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  /**
   * Check if resolution is within a specific tier
   * @param {string} tierToCheck - Tier from RESOLUTION_TIERS
   * @returns {boolean}
   */
  const isTier = useCallback((tierToCheck) => {
    return tier === tierToCheck;
  }, [tier]);

  /**
   * Check if resolution is at least a specific tier
   * @param {string} minTier - Minimum tier from RESOLUTION_TIERS
   * @returns {boolean}
   */
  const isAtLeast = useCallback((minTier) => {
    const tierOrder = [
      RESOLUTION_TIERS.LAPTOP,
      RESOLUTION_TIERS.FHD,
      RESOLUTION_TIERS.QHD,
      RESOLUTION_TIERS.FOUR_K,
      RESOLUTION_TIERS.FOUR_K_PLUS,
    ];
    
    const currentIndex = tierOrder.indexOf(tier);
    const minIndex = tierOrder.indexOf(minTier);
    
    return currentIndex >= minIndex;
  }, [tier]);

  /**
   * Get scaled value based on current scale factor
   * @param {number} baseValue - Base value in pixels
   * @returns {number} - Scaled value
   */
  const scaleValue = useCallback((baseValue) => {
    return baseValue * scaleFactor;
  }, [scaleFactor]);

  /**
   * Get responsive value based on current tier
   * @param {Object} values - Object with values for each tier
   * @returns {any} - Value for current tier
   * 
   * Example:
   * getResponsiveValue({
   *   laptop: 16,
   *   fhd: 18,
   *   qhd: 20,
   *   '4k': 24,
   *   '4k_plus': 28
   * })
   */
  const getResponsiveValue = useCallback((values) => {
    // Try to get exact match first
    if (values[tier] !== undefined) {
      return values[tier];
    }
    
    // Fall back to closest available tier
    const tierOrder = [
      RESOLUTION_TIERS.LAPTOP,
      RESOLUTION_TIERS.FHD,
      RESOLUTION_TIERS.QHD,
      RESOLUTION_TIERS.FOUR_K,
      RESOLUTION_TIERS.FOUR_K_PLUS,
    ];
    
    const currentIndex = tierOrder.indexOf(tier);
    
    // Look backwards for a defined value
    for (let i = currentIndex; i >= 0; i--) {
      const checkTier = tierOrder[i];
      if (values[checkTier] !== undefined) {
        return values[checkTier];
      }
    }
    
    // Fallback to first defined value
    for (const tierKey of tierOrder) {
      if (values[tierKey] !== undefined) {
        return values[tierKey];
      }
    }
    
    return null;
  }, [tier]);

  // ============================================
  // RETURN OBJECT
  // ============================================
  return {
    // Dimensions
    screenWidth,
    screenHeight,
    orientation,
    
    // Scale information
    scaleFactor,
    tier,
    resolutionInfo,
    
    // Boolean checks
    is4K,
    isAssemblyLine,
    isLaptop: tier === RESOLUTION_TIERS.LAPTOP,
    isFHD: tier === RESOLUTION_TIERS.FHD,
    isQHD: tier === RESOLUTION_TIERS.QHD,
    is4KStandard: tier === RESOLUTION_TIERS.FOUR_K,
    is4KPlus: tier === RESOLUTION_TIERS.FOUR_K_PLUS,
    
    // Utility functions
    isTier,
    isAtLeast,
    scaleValue,
    getResponsiveValue,
  };
};

/**
 * Hook for simple scale factor only (lightweight version)
 * @returns {number} Current scale factor
 */
export const useScaleFactor = () => {
  const [scaleFactor, setScaleFactor] = useState(1.0);

  useEffect(() => {
    const updateScale = () => {
      const width = window.innerWidth;
      setScaleFactor(getScaleFactor(width));
    };

    updateScale();
    window.addEventListener('resize', updateScale);

    return () => {
      window.removeEventListener('resize', updateScale);
    };
  }, []);

  return scaleFactor;
};

/**
 * Hook to check if current resolution is assembly line ready
 * @returns {boolean}
 */
export const useIsAssemblyLine = () => {
  const [isAssemblyLine, setIsAssemblyLine] = useState(false);

  useEffect(() => {
    const updateCheck = () => {
      const width = window.innerWidth;
      setIsAssemblyLine(isAssemblyLineResolution(width));
    };

    updateCheck();
    window.addEventListener('resize', updateCheck);

    return () => {
      window.removeEventListener('resize', updateCheck);
    };
  }, []);

  return isAssemblyLine;
};

export default useResolutionScale;

